import { useEffect, useState } from "react";
import type { Video } from "@/types/video";
import { getEpisodeLabel } from "@/lib/videoData";

interface DemoPlayerProps {
  video: Video;
  episode: number;
}

function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function DemoPlayer({ video, episode }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const parsed = Number.parseInt(video.duration, 10);
  const totalSeconds = (Number.isFinite(parsed) && parsed > 0 ? parsed : 45) * 60;

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
  }, [episode, video.id]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return Math.min(100, prev + 0.4);
      });
    }, 200);
    return () => window.clearInterval(timer);
  }, [playing]);

  useEffect(() => {
    if (progress >= 100 && playing) setPlaying(false);
  }, [progress, playing]);

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setProgress(ratio * 100);
  };

  const episodeLabel = getEpisodeLabel(video);

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-primary-950">
        <img
          src={video.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-35 blur-md"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/60 to-primary-950/40" />

        <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded bg-accent-500 px-2 py-1 text-[10px] font-bold text-foreground-950 md:left-4 md:top-4">
          <i className="ri-error-warning-line text-xs" />
          演示播放器
        </span>

        <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center">
          <button
            type="button"
            onClick={() => setPlaying((prev) => !prev)}
            aria-label={playing ? "暂停演示" : "播放演示"}
            className="group flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-foreground-950 transition-transform duration-300 hover:scale-105 md:h-20 md:w-20"
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl md:text-4xl`} />
          </button>
          <p className="mt-4 font-heading text-lg font-bold text-background-50 md:text-xl">
            {video.title}
            {video.episodeCount > 0 ? ` · 第 ${episode} ${episodeLabel}` : ""}
          </p>
          <p className="mt-1.5 max-w-md text-[11px] leading-relaxed text-background-300/90 md:text-xs">
            此为前端界面演示，未接入真实片源。播放进度与画面均为模拟效果，不产生真实播放数据。
          </p>
          {playing && (
            <p className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-accent-300">
              <span className="h-1.5 w-1.5 animate-soft-pulse rounded-full bg-accent-400" />
              演示播放中
            </p>
          )}
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-primary-950 to-transparent px-3 pb-3 pt-8 md:px-4 md:pb-4">
          <div
            role="slider"
            tabIndex={0}
            aria-label="播放进度"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            onClick={handleSeek}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight") setProgress((prev) => Math.min(100, prev + 5));
              if (event.key === "ArrowLeft") setProgress((prev) => Math.max(0, prev - 5));
            }}
            className="group relative h-1.5 w-full cursor-pointer rounded-full bg-background-50/25"
          >
            <div className="h-full rounded-full bg-accent-500" style={{ width: `${progress}%` }} />
            <span
              className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-accent-300 opacity-0 transition-opacity group-hover:opacity-100"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-background-300">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPlaying((prev) => !prev)}
                aria-label={playing ? "暂停" : "播放"}
                className="cursor-pointer text-base text-background-100 transition-colors hover:text-accent-300"
              >
                <i className={playing ? "ri-pause-line" : "ri-play-line"} />
              </button>
              <span className="font-heading tabular-nums">
                {formatTime((progress / 100) * totalSeconds)} / {formatTime(totalSeconds)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden items-center gap-1 sm:flex">
                <i className="ri-hd-line text-sm" />
                HD 演示
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-volume-up-line text-sm" />
                模拟音轨
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-2.5 flex items-start gap-1.5 rounded-md bg-secondary-100/70 px-3 py-2 text-[11px] leading-relaxed text-secondary-900">
        <i className="ri-information-line mt-0.5 text-sm text-secondary-600" />
        说明：糖豆视频当前为内容导览演示站，播放器仅展示交互样式，未绑定任何真实视频地址。
      </p>
    </div>
  );
}